import React, { useState, useEffect } from 'react';
import { pagesAPI } from '../../services/api';
import { 
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  MessageCircle,
  Send,
  MapPin,
  Video,
  PhoneCall,
  Users,
  Shield,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Settings,
  FileText
} from 'lucide-react';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const AppointmentPageManagement = () => {
  const { notify } = useNotificationHook();
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('header');
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [showAddBenefit, setShowAddBenefit] = useState(false);

  const defaultContent = {
    // Header Section
    main_title: 'Schedule a',
    gradient_title: 'Meeting',
    description: 'Book a consultation with our team to discuss your project requirements and get expert guidance.',
    
    // Form Section
    form_title: 'Schedule Your Meeting',
    form_description: 'Fill out the form below and we\'ll confirm your appointment within 2 hours.',
    
    // Form Fields
    form_fields: {
      name: {
        label: 'Full Name',
        placeholder: 'Enter your full name'
      },
      email: {
        label: 'Email Address',
        placeholder: 'Enter your email address'
      },
      phone: {
        label: 'Phone Number',
        placeholder: 'Enter your phone number'
      },
      date: {
        label: 'Preferred Date',
        placeholder: 'Select date'
      },
      time: {
        label: 'Preferred Time',
        placeholder: 'Select time'
      },
      purpose: {
        label: 'Meeting Purpose',
        placeholder: 'Select purpose'
      },
      message: {
        label: 'Additional Message',
        placeholder: 'Tell us more about your project or any specific requirements...'
      }
    },
    
    // Time Options
    time_options: [
      { value: '09:00', label: '9:00 AM' },
      { value: '10:00', label: '10:00 AM' },
      { value: '11:00', label: '11:00 AM' },
      { value: '12:00', label: '12:00 PM' },
      { value: '13:00', label: '1:00 PM' },
      { value: '14:00', label: '2:00 PM' },
      { value: '15:00', label: '3:00 PM' },
      { value: '16:00', label: '4:00 PM' },
      { value: '17:00', label: '5:00 PM' }
    ],
    
    // Purpose Options
    purpose_options: [
      { value: 'project-discussion', label: 'Project Discussion' },
      { value: 'consultation', label: 'General Consultation' },
      { value: 'quote-request', label: 'Quote Request' },
      { value: 'technical-support', label: 'Technical Support' },
      { value: 'partnership', label: 'Partnership Discussion' },
      { value: 'other', label: 'Other' }
    ],
    
    // Submit Button
    submit_button: {
      text: 'Schedule Meeting',
      icon: 'Send'
    },
    
    // Benefits Section
    benefits_title: 'Why Schedule a Meeting?',
    benefits: [
      {
        id: 1,
        icon: 'Users',
        title: 'Expert Consultation',
        description: 'Get personalized advice from our experienced team'
      },
      {
        id: 2,
        icon: 'Video',
        title: 'Video Call',
        description: 'Meet face-to-face via video conference'
      },
      {
        id: 3,
        icon: 'Calendar',
        title: 'Flexible Scheduling',
        description: 'Choose a time that works best for you'
      },
      {
        id: 4,
        icon: 'Shield',
        title: 'Free Consultation',
        description: 'No cost for initial project discussion'
      }
    ],
    
    // Contact Info
    contact_info: {
      title: 'Get in Touch',
      phone: '+91 9876543210',
      email: 'info@niswainfotech.com',
      address: '123 Business District, Andheri East, Mumbai, Maharashtra 400069'
    },
    
    // CTA Section
    cta_title: 'Need Immediate Assistance?',
    cta_description: 'Can\'t wait for a scheduled meeting? Contact us directly for urgent inquiries.',
    cta_buttons: [
      {
        title: 'Contact Us',
        icon: 'MessageCircle',
        link: '/contact'
      },
      {
        title: 'Call Now',
        icon: 'PhoneCall',
        link: 'tel:+919876543210'
      }
    ]
  };

  const availableIcons = [
    'Calendar', 'Clock', 'User', 'Mail', 'Phone', 'MessageCircle', 'Send',
    'MapPin', 'Video', 'PhoneCall', 'Users', 'Shield', 'Eye', 'Settings'
  ];

  const fetchPageContent = async () => {
    try {
      setLoading(true);
      const response = await pagesAPI.getByType('appointment');
      
      if (response?.data?.success) {
        const content = response.data.data.content || defaultContent;
        setPageContent(content);
      } else {
        setPageContent(defaultContent);
      }
    } catch (error) {
      console.error('Error fetching appointment page:', error);
      setPageContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  const updatePageContent = async () => {
    try {
      setSaving(true);
      const response = await pagesAPI.updateByType('appointment', {
        content: pageContent
      });
      
              if (response?.data?.success) {
          notify.success('Appointment page updated successfully', 'Success');
        }
      } catch (error) {
        console.error('Error updating appointment page:', error);
        notify.error('Failed to update appointment page', 'Update Error');
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (section, key, value) => {
    setPageContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const handleFormFieldChange = (field, key, value) => {
    setPageContent(prev => ({
      ...prev,
      form_fields: {
        ...prev.form_fields,
        [field]: {
          ...prev.form_fields[field],
          [key]: value
        }
      }
    }));
  };

  const handleArrayChange = (section, newArray) => {
    setPageContent(prev => ({
      ...prev,
      [section]: newArray
    }));
  };

  const addBenefit = () => {
    const newBenefit = {
      id: Date.now(),
      icon: 'Users',
      title: 'New Benefit',
      description: 'Enter benefit description'
    };
    
    setPageContent(prev => ({
      ...prev,
      benefits: [...prev.benefits, newBenefit]
    }));
    setShowAddBenefit(false);
  };

  const updateBenefit = (index, updatedBenefit) => {
    setPageContent(prev => ({
      ...prev,
      benefits: prev.benefits.map((benefit, i) => 
        i === index ? updatedBenefit : benefit
      )
    }));
  };

  const saveBenefit = (index, benefit) => {
    setPageContent(prev => ({
      ...prev,
      benefits: prev.benefits.map((b, i) => 
        i === index ? benefit : b
      )
    }));
    setEditingBenefit(null);
  };

  const deleteBenefit = (index) => {
    if (window.confirm('Are you sure you want to delete this benefit? This action cannot be undone.')) {
      setPageContent(prev => ({
        ...prev,
        benefits: prev.benefits.filter((_, i) => i !== index)
      }));
    }
  };

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  useEffect(() => {
    fetchPageContent();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              Appointment Page Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage appointment page content and settings
            </p>
          </div>
          <button
            onClick={updatePageContent}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex justify-between items-center">
            <span>{message.text}</span>
            <button onClick={clearMessage} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 sm:p-6">
        {/* Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Sections
            </h3>
            <nav className="space-y-2">
              {[
                { id: 'header', label: 'Header Section', icon: FileText },
                { id: 'form', label: 'Form Section', icon: User },
                { id: 'benefits', label: 'Benefits Section', icon: Shield },
                { id: 'contact', label: 'Contact Info', icon: Phone },
                { id: 'cta', label: 'CTA Section', icon: MessageCircle }
              ].map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveTab(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeTab === section.id
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Editor */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            {activeTab === 'header' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Header Section
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Main Title
                  </label>
                  <input
                    type="text"
                    value={pageContent.main_title}
                    onChange={(e) => setPageContent(prev => ({ ...prev, main_title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Schedule a"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gradient Title
                  </label>
                  <input
                    type="text"
                    value={pageContent.gradient_title}
                    onChange={(e) => setPageContent(prev => ({ ...prev, gradient_title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Meeting"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={pageContent.description}
                    onChange={(e) => setPageContent(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    rows="3"
                    placeholder="Enter page description"
                  />
                </div>
              </div>
            )}

            {activeTab === 'form' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Form Section
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Form Title
                  </label>
                  <input
                    type="text"
                    value={pageContent.form_title}
                    onChange={(e) => setPageContent(prev => ({ ...prev, form_title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Schedule Your Meeting"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Form Description
                  </label>
                  <textarea
                    value={pageContent.form_description}
                    onChange={(e) => setPageContent(prev => ({ ...prev, form_description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    rows="2"
                    placeholder="Enter form description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Submit Button Text
                  </label>
                  <input
                    type="text"
                    value={pageContent.submit_button.text}
                    onChange={(e) => setPageContent(prev => ({ 
                      ...prev, 
                      submit_button: { ...prev.submit_button, text: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Schedule Meeting"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Submit Button Icon
                  </label>
                  <select
                    value={pageContent.submit_button.icon}
                    onChange={(e) => setPageContent(prev => ({ 
                      ...prev, 
                      submit_button: { ...prev.submit_button, icon: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {availableIcons.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>

                <div className="border-t pt-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    Form Fields
                  </h4>
                  <div className="space-y-4">
                    {Object.keys(pageContent.form_fields).map(field => (
                      <div key={field} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-3 capitalize">
                          {field} Field
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Label
                            </label>
                            <input
                              type="text"
                              value={pageContent.form_fields[field].label}
                              onChange={(e) => handleFormFieldChange(field, 'label', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Placeholder
                            </label>
                            <input
                              type="text"
                              value={pageContent.form_fields[field].placeholder}
                              onChange={(e) => handleFormFieldChange(field, 'placeholder', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    Time Options
                  </h4>
                  <div className="space-y-2">
                    {pageContent.time_options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={option.value}
                          onChange={(e) => {
                            const newOptions = [...pageContent.time_options];
                            newOptions[index].value = e.target.value;
                            handleArrayChange('time_options', newOptions);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="Value (e.g., 09:00)"
                        />
                        <input
                          type="text"
                          value={option.label}
                          onChange={(e) => {
                            const newOptions = [...pageContent.time_options];
                            newOptions[index].label = e.target.value;
                            handleArrayChange('time_options', newOptions);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="Label (e.g., 9:00 AM)"
                        />
                        <button
                          onClick={() => {
                            const newOptions = pageContent.time_options.filter((_, i) => i !== index);
                            handleArrayChange('time_options', newOptions);
                          }}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newOptions = [...pageContent.time_options, { value: '', label: '' }];
                        handleArrayChange('time_options', newOptions);
                      }}
                      className="w-full px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
                    >
                      <Plus className="w-4 h-4 inline mr-2" />
                      Add Time Option
                    </button>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    Purpose Options
                  </h4>
                  <div className="space-y-2">
                    {pageContent.purpose_options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={option.value}
                          onChange={(e) => {
                            const newOptions = [...pageContent.purpose_options];
                            newOptions[index].value = e.target.value;
                            handleArrayChange('purpose_options', newOptions);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="Value (e.g., project-discussion)"
                        />
                        <input
                          type="text"
                          value={option.label}
                          onChange={(e) => {
                            const newOptions = [...pageContent.purpose_options];
                            newOptions[index].label = e.target.value;
                            handleArrayChange('purpose_options', newOptions);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="Label (e.g., Project Discussion)"
                        />
                        <button
                          onClick={() => {
                            const newOptions = pageContent.purpose_options.filter((_, i) => i !== index);
                            handleArrayChange('purpose_options', newOptions);
                          }}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newOptions = [...pageContent.purpose_options, { value: '', label: '' }];
                        handleArrayChange('purpose_options', newOptions);
                      }}
                      className="w-full px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
                    >
                      <Plus className="w-4 h-4 inline mr-2" />
                      Add Purpose Option
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'benefits' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Benefits Section
                  </h3>
                  <button
                    onClick={() => setShowAddBenefit(true)}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-lg flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Benefit
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Benefits Title
                  </label>
                  <input
                    type="text"
                    value={pageContent.benefits_title}
                    onChange={(e) => setPageContent(prev => ({ ...prev, benefits_title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Why Schedule a Meeting?"
                  />
                </div>

                <div className="space-y-4">
                  {pageContent.benefits.map((benefit, index) => (
                    <div key={benefit.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      {editingBenefit === index ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Icon
                              </label>
                              <select
                                value={benefit.icon}
                                onChange={(e) => {
                                  const updatedBenefit = { ...benefit, icon: e.target.value };
                                  setPageContent(prev => ({
                                    ...prev,
                                    benefits: prev.benefits.map((b, i) => 
                                      i === index ? updatedBenefit : b
                                    )
                                  }));
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                              >
                                {availableIcons.map(icon => (
                                  <option key={icon} value={icon}>{icon}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Title
                              </label>
                              <input
                                type="text"
                                value={benefit.title}
                                onChange={(e) => {
                                  const updatedBenefit = { ...benefit, title: e.target.value };
                                  setPageContent(prev => ({
                                    ...prev,
                                    benefits: prev.benefits.map((b, i) => 
                                      i === index ? updatedBenefit : b
                                    )
                                  }));
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description
                              </label>
                              <input
                                type="text"
                                value={benefit.description}
                                onChange={(e) => {
                                  const updatedBenefit = { ...benefit, description: e.target.value };
                                  setPageContent(prev => ({
                                    ...prev,
                                    benefits: prev.benefits.map((b, i) => 
                                      i === index ? updatedBenefit : b
                                    )
                                  }));
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setEditingBenefit(null)}
                              className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => saveBenefit(index, benefit)}
                              className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                              <Shield className="w-5 h-5 text-primary-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {benefit.title}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {benefit.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingBenefit(index)}
                              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteBenefit(index)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add Benefit Modal */}
                {showAddBenefit && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Add New Benefit
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Icon
                          </label>
                          <select
                            value="Users"
                            onChange={(e) => {
                              const newBenefit = {
                                id: Date.now(),
                                icon: e.target.value,
                                title: 'New Benefit',
                                description: 'Enter benefit description'
                              };
                              setPageContent(prev => ({
                                ...prev,
                                benefits: [...prev.benefits, newBenefit]
                              }));
                              setShowAddBenefit(false);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          >
                            {availableIcons.map(icon => (
                              <option key={icon} value={icon}>{icon}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setShowAddBenefit(false)}
                            className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={addBenefit}
                            className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                          >
                            Add Benefit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Contact Information
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Title
                  </label>
                  <input
                    type="text"
                    value={pageContent.contact_info.title}
                    onChange={(e) => setPageContent(prev => ({ 
                      ...prev, 
                      contact_info: { ...prev.contact_info, title: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Get in Touch"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={pageContent.contact_info.phone}
                    onChange={(e) => setPageContent(prev => ({ 
                      ...prev, 
                      contact_info: { ...prev.contact_info, phone: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., +91 9876543210"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={pageContent.contact_info.email}
                    onChange={(e) => setPageContent(prev => ({ 
                      ...prev, 
                      contact_info: { ...prev.contact_info, email: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., info@niswainfotech.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Address
                  </label>
                  <textarea
                    value={pageContent.contact_info.address}
                    onChange={(e) => setPageContent(prev => ({ 
                      ...prev, 
                      contact_info: { ...prev.contact_info, address: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    rows="3"
                    placeholder="Enter full address"
                  />
                </div>
              </div>
            )}

            {activeTab === 'cta' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Call-to-Action Section
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CTA Title
                  </label>
                  <input
                    type="text"
                    value={pageContent.cta_title}
                    onChange={(e) => setPageContent(prev => ({ ...prev, cta_title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Need Immediate Assistance?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CTA Description
                  </label>
                  <textarea
                    value={pageContent.cta_description}
                    onChange={(e) => setPageContent(prev => ({ ...prev, cta_description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    rows="3"
                    placeholder="Enter CTA description"
                  />
                </div>

                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    CTA Buttons
                  </h4>
                  <div className="space-y-4">
                    {pageContent.cta_buttons.map((button, index) => (
                      <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Button Title
                            </label>
                            <input
                              type="text"
                              value={button.title}
                              onChange={(e) => {
                                const newButtons = [...pageContent.cta_buttons];
                                newButtons[index].title = e.target.value;
                                handleArrayChange('cta_buttons', newButtons);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Icon
                            </label>
                            <select
                              value={button.icon}
                              onChange={(e) => {
                                const newButtons = [...pageContent.cta_buttons];
                                newButtons[index].icon = e.target.value;
                                handleArrayChange('cta_buttons', newButtons);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                              {availableIcons.map(icon => (
                                <option key={icon} value={icon}>{icon}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Link
                            </label>
                            <input
                              type="text"
                              value={button.link}
                              onChange={(e) => {
                                const newButtons = [...pageContent.cta_buttons];
                                newButtons[index].link = e.target.value;
                                handleArrayChange('cta_buttons', newButtons);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                              placeholder="e.g., /contact or tel:+919876543210"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const newButtons = pageContent.cta_buttons.filter((_, i) => i !== index);
                            handleArrayChange('cta_buttons', newButtons);
                          }}
                          className="mt-2 px-3 py-1 text-red-600 hover:bg-red-100 rounded-lg text-sm"
                        >
                          Remove Button
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newButtons = [...pageContent.cta_buttons, { title: '', icon: 'MessageCircle', link: '' }];
                        handleArrayChange('cta_buttons', newButtons);
                      }}
                      className="w-full px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
                    >
                      <Plus className="w-4 h-4 inline mr-2" />
                      Add CTA Button
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentPageManagement; 